/**
 * The operating-statement lines the chart of accounts could not place.
 *
 * Register K5: "line items outside the schema are accepted into a typed
 * overflow bucket rather than rejected." The engine has done that for a while —
 * every line classified, the original label kept verbatim, nothing dropped.
 * What it could not do was reach anyone: no screen read the overflow, so a
 * reviewer could not see that three lines fell outside the schema and could not
 * do anything about them. From their chair that is indistinguishable from the
 * lines having been rejected, which is what the bucket exists to avoid.
 *
 * This is the reviewer's side of it. Unplaced lines lead, because they are the
 * ones that need a person; placing one is a select, never free text, because
 * the overflow exists so a line is never lost, not so a category can be
 * invented at the point of a miss.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'

import api from '@/services/api'

interface LineItem {
  id: string
  sortOrder: number
  originalLabel: string
  category: string
  section: string
  isKnown: boolean
  amount: number | null
}

interface StatementSummary {
  id: string
  periodLabel: string | null
  source: string | null
  totalCount: number
  knownCount: number
  unknownCount: number
  createdAt: string | null
}

interface StatementDetail extends StatementSummary {
  lineItems: LineItem[]
}

const money = (n: number | null) =>
  n === null || n === undefined
    ? '—'
    : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

export default function OperatingStatementPanel({ loanId }: { loanId: string }) {
  const queryClient = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['operating-statements', loanId],
    queryFn: async () => {
      const res = await api.get<{ statements: StatementSummary[] }>(
        `/operating-statements/loans/${loanId}`,
      )
      return res.data.statements
    },
  })

  const { data: detail } = useQuery({
    queryKey: ['operating-statement', openId],
    queryFn: async () => {
      const res = await api.get<StatementDetail>(`/operating-statements/${openId}`)
      return res.data
    },
    enabled: Boolean(openId),
  })

  const { data: categories } = useQuery({
    queryKey: ['operating-statement-categories'],
    queryFn: async () => {
      const res = await api.get<{ categories: { key: string; label: string }[] }>(
        '/operating-statements/categories',
      )
      return res.data.categories
    },
    staleTime: Infinity,
  })

  const place = useMutation({
    mutationFn: async ({ id, category }: { id: string; category: string }) => {
      await api.patch(`/operating-statements/line-items/${id}`, { category })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operating-statement', openId] })
      queryClient.invalidateQueries({ queryKey: ['operating-statements', loanId] })
    },
  })

  if (isLoading) return <div className="text-[10px] text-stone-500">Reading statements…</div>
  if (isError) {
    return (
      <div className="text-[10px] text-stone-500">
        Operating statements could not be read. The figures above are unaffected.
      </div>
    )
  }
  if (!data?.length) {
    return (
      <div className="text-[10px] text-stone-500">
        No operating statement on this deal yet.
      </div>
    )
  }

  const unplaced = (detail?.lineItems ?? []).filter((li) => !li.isKnown)
  const placed = (detail?.lineItems ?? []).filter((li) => li.isKnown)

  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-medium text-stone-200">Operating statements</h3>

      <table className="w-full text-[10px]">
        <thead>
          <tr className="border-b border-stone-800 text-stone-500">
            <th scope="col" className="py-1 text-left font-medium">Period</th>
            <th scope="col" className="py-1 text-right font-medium">Lines</th>
            <th scope="col" className="py-1 text-right font-medium">Need a category</th>
            <th scope="col" className="py-1" />
          </tr>
        </thead>
        <tbody>
          {data.map((s) => (
            <tr key={s.id} className="border-b border-stone-800/60 last:border-0">
              <td className="py-1 text-stone-200">{s.periodLabel || s.source || s.id}</td>
              <td className="py-1 text-right tabular-nums text-stone-300">{s.totalCount}</td>
              <td className="py-1 text-right tabular-nums">
                {s.unknownCount > 0 ? (
                  <span className="inline-flex items-center gap-1 text-amber-300">
                    <AlertTriangle className="h-3 w-3" aria-hidden />
                    {s.unknownCount}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-emerald-300">
                    <CheckCircle2 className="h-3 w-3" aria-hidden />0
                  </span>
                )}
              </td>
              <td className="py-1 text-right">
                <button
                  type="button"
                  onClick={() => setOpenId(openId === s.id ? null : s.id)}
                  aria-expanded={openId === s.id}
                  className="text-stone-400 underline-offset-2 hover:text-amber-200 hover:underline"
                >
                  {openId === s.id ? 'Hide lines' : 'Show lines'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {openId && detail && (
        <div className="space-y-2 border-t border-stone-800 pt-2">
          {unplaced.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] text-amber-200">
                {unplaced.length} line{unplaced.length === 1 ? '' : 's'} outside the chart of
                accounts. Nothing was discarded — each is kept exactly as the statement worded
                it, and needs a category before it counts anywhere.
              </p>
              <table className="w-full text-[10px]">
                <tbody>
                  {unplaced.map((li) => (
                    <tr key={li.id} className="border-b border-stone-800/40 last:border-0">
                      <td className="py-1 pr-2 text-stone-200">{li.originalLabel}</td>
                      <td className="py-1 pr-2 text-right tabular-nums text-stone-300">
                        {money(li.amount)}
                      </td>
                      <td className="py-1">
                        <label className="sr-only" htmlFor={`cat-${li.id}`}>
                          Category for {li.originalLabel}
                        </label>
                        <select
                          id={`cat-${li.id}`}
                          defaultValue=""
                          disabled={place.isPending}
                          onChange={(e) =>
                            e.target.value && place.mutate({ id: li.id, category: e.target.value })
                          }
                          className="w-full rounded border border-stone-700 bg-transparent px-1 py-0.5 text-[10px] text-stone-200"
                        >
                          <option value="">Assign a category…</option>
                          {(categories ?? []).map((c) => (
                            <option key={c.key} value={c.key}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {place.isError && (
                <p className="text-[10px] text-red-300">
                  That category was not accepted — the line is unchanged.
                </p>
              )}
            </div>
          )}

          <details>
            <summary className="cursor-pointer text-[10px] text-stone-500">
              {placed.length} line{placed.length === 1 ? '' : 's'} already placed
            </summary>
            <table className="mt-1 w-full text-[10px]">
              <tbody>
                {placed.map((li) => (
                  <tr key={li.id} className="border-b border-stone-800/40 last:border-0">
                    <td className="py-0.5 pr-2 text-stone-300">{li.originalLabel}</td>
                    <td className="py-0.5 pr-2 text-stone-500">{li.category}</td>
                    <td className="py-0.5 text-right tabular-nums text-stone-400">
                      {money(li.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      )}
    </section>
  )
}
